import React, { useMemo } from "react";
import { Card, Grid, Select, Typography } from "antd";
import { PieChart, Pie, Cell } from "recharts";

const { Text } = Typography;

export default function AttendanceOverviewPanel({
  attendanceReport = [],
  pendingRequests = {},
  employees,
  rangeType,
  onRangeChange
}) {

const screens = Grid.useBreakpoint();    

const stats = useMemo(() => {
  let present = 0;
  let late = 0;
  let absent = 0;
  let pending = 0;

  const ABSENCE_LIKE = [
    "absence",
    "leave_with_pay",
    "leave_without_pay",
    "sick_leave",
    "casual_leave",
    "annual_leave",
  ];

  // Each row classified into exactly ONE of present/absent/pending (Late is
  // a subset of Present, tracked separately below) — all three now come
  // from this SAME attendanceReport array, so the percentages and the
  // "Total Attendance" count can never disagree with each other again.
  // Previously "pending" came from an entirely different, date-range-
  // independent global approvals queue (pendingRequests), which is why the
  // Today/This Month percentages never matched their own displayed total —
  // and "absence" values didn't check status at all, so a rejected or still
  // -pending leave request was being counted as a finalized Absence.
  attendanceReport.forEach(a => {
    if (a.status === "pending") {
      pending++;
      return;
    }
    if (a.status === "rejected") {
      // A rejected leave/absence request never happened — don't count it.
      return;
    }
    if (a.attendance === "present") {
      present++;
      if (a.check_in_status?.toLowerCase().includes("late")) {
        late++;
      }
      return;
    }
    if (ABSENCE_LIKE.includes(a.attendance) && a.status === "approved") {
      absent++;
    }
  });

  const total = present + absent + pending || 1;

  // ✅ Late % based on present
  const latePercent = present ? Math.round((late / present) * 100) : 0;

  return [
    {
      name: "Present",
      value: present,
      percent: Math.round((present / total) * 100),
      color: "#18c22f",
    },
    {
      name: "Late",
      value: late,
      percent: latePercent, // ✅ special logic
      color: "#1e5aa8",
    },
    {
      name: "Absent",
      value: absent,
      percent: Math.round((absent / total) * 100),
      color: "#c62828",
    },
    {
      name: "Pending",
      value: pending,
      percent: Math.round((pending / total) * 100),
      color: "#f3b704",
    },
  ];
}, [attendanceReport]);


const pieData = stats.filter(s => s.name !== "Late");

// Sum of the actual pie slices (Present + Absent + Pending — Late is a
// subset of Present, not a separate slice, so it's excluded here the same
// way it's excluded from pieData) — previously this was `employees.length`,
// the size of the full active-employee roster regardless of date range,
// which never matched what the percentages below were computed from.
const totalAttendance = pieData.reduce((sum, s) => sum + s.value, 0);

const lateData = [
  {
    name: "Late",
    value: stats.find(s => s.name === "Late")?.value || 0,
  },
  {
    name: "Remaining Present",
    value:
      (stats.find(s => s.name === "Present")?.value || 0) -
      (stats.find(s => s.name === "Late")?.value || 0),
  },
];

return (

<Card
title="Attendance Overview"
extra={<Text type="secondary">
    <Select
  value={rangeType}
  size="small"
  onChange={onRangeChange}
  options={[
    { value: "today", label: "Today" },
    { value: "month", label: "This Month" }
  ]}
/>
</Text>}
>

{/* Chart */}

<div style={{display:"flex",justifyContent:"center"}}>

<div style={{position:"relative"}}>

<PieChart width={screens.xs ? 240 : 300} height={screens.xs ? 160 : 190}>

  {/* 🔵 MAIN PIE (normal thickness) */}
  <Pie
    data={pieData}
    cx="50%"
    cy="100%"
    startAngle={180}
    endAngle={0}
    innerRadius={screens.xs ? 50 : 60}
    outerRadius={screens.xs ? 85 : 100}
    paddingAngle={3}
    cornerRadius={6}
    dataKey="value"
  >
    {pieData.map((entry, i) => (
      <Cell key={i} fill={entry.color} />
    ))}
  </Pie>

  {/* 🟡 INNER THIN PIE (Late subset of Present) */}
  <Pie
    data={lateData}
    cx="50%"
    cy="100%"
    startAngle={180}
    endAngle={0}
    innerRadius={screens.xs ? 50 : 60}   
    outerRadius={screens.xs ? 65 : 80}   
    dataKey="value"
  >
    <Cell fill="#1e5aa8" /> {/* Late */}
    <Cell fill="#e0e0e0" /> {/* Remaining present */}
  </Pie>

</PieChart>

{/* Center text */}

<div
style={{
position:"absolute",
top:141,
left:"50%",
transform:"translateX(-50%)",
textAlign:"center"
}}
>

<Text  className = "totalAttendance" type="secondary" style={{fontSize:screens.xs ? 8: 12}}>
Total Attendance
</Text>

<div style={{fontWeight:600,fontSize: screens.xs ? 10: 18}}>
{totalAttendance}
</div>

</div>

</div>

</div>

{/* Legend below */}

<div style={{marginTop:16}}>

<Text strong>Status</Text>

{stats.map(s => (

<div
key={s.name}
style={{
display:"flex",
justifyContent:"space-between",
marginTop:8
}}
>

<Text>
<span
style={{
display:"inline-block",
width:8,
height:8,
background:s.color,
borderRadius:4,
marginRight:6
}}
/>
{s.name}
</Text>

<Text>{s.percent}%</Text>

</div>

))}

</div>

</Card>

);

}